import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useScreen } from '../../context/ScreenProvider';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import '../../styles/BottomSheet.css';

/**
 * Reusable modal wrapper: centered modal on desktop, bottom sheet on mobile.
 *
 * Props:
 *   onClose    – called when the modal should close
 *   className  – CSS class for the desktop modal container
 *   zIndex     – optional z-index for the overlay (default 99)
 *   children   – either ReactNode or render function ({ onClose }) => ReactNode
 *                The render function receives onClose that animates on mobile.
 */
export default function BottomSheet({ onClose, children, className, zIndex }) {
    const { isMobile } = useScreen();
    const sheetRef = useRef(null);
    const dragState = useRef(null);
    const [dragOffset, setDragOffset] = useState(0);
    const [closing, setClosing] = useState(false);       // X / overlay / ESC close
    const [swipeDismiss, setSwipeDismiss] = useState(false); // swipe-to-dismiss close

    useBodyScrollLock(true);

    // CSS-animation close (slide from top to off-screen)
    const animateClose = useCallback(() => {
        setClosing(true);
        setTimeout(onClose, 250);
    }, [onClose]);

    // Unified close: animated on mobile, instant on desktop
    const handleClose = useCallback(() => {
        if (isMobile) {
            animateClose();
        } else {
            onClose();
        }
    }, [isMobile, animateClose, onClose]);

    // ESC key to close
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') handleClose();
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [handleClose]);

    // Swipe-to-dismiss gesture (mobile only)
    const handleTouchStart = useCallback((e) => {
        const el = sheetRef.current;
        if (!el || el.scrollTop > 0) return;
        dragState.current = { startY: e.touches[0].clientY, moved: false };
    }, []);

    const handleTouchMove = useCallback((e) => {
        if (!dragState.current) return;
        const dy = e.touches[0].clientY - dragState.current.startY;
        if (dy > 0) {
            dragState.current.moved = true;
            setDragOffset(dy);
            e.preventDefault();
        }
    }, []);

    const handleTouchEnd = useCallback(() => {
        if (!dragState.current) return;
        if (dragState.current.moved && dragOffset > 100) {
            // Transition from current drag position to off-screen (no CSS animation snap-back)
            setSwipeDismiss(true);
            setTimeout(onClose, 250);
        } else {
            setDragOffset(0);
        }
        dragState.current = null;
    }, [dragOffset, onClose]);

    // Build class names
    const isClosing = closing || swipeDismiss;
    const overlayClass = `bs-overlay${isMobile ? ' bs-overlay--mobile' : ''}${isClosing ? ' bs-overlay--closing' : ''}`;

    const containerClass = isMobile
        ? `bs-sheet${closing ? ' bs-sheet--closing' : ''}`
        : className || '';

    // Swipe dismiss: CSS transition from current drag position to off-screen
    // Button/overlay close: CSS animation handles it (no inline style needed)
    const containerStyle = (() => {
        if (!isMobile) return undefined;
        if (swipeDismiss) return { transform: 'translateY(100%)', transition: 'transform 0.25s ease-in' };
        if (dragOffset > 0) return { transform: `translateY(${dragOffset}px)` };
        return undefined;
    })();

    const content = typeof children === 'function'
        ? children({ onClose: handleClose })
        : children;

    return createPortal(
        <div
            className={overlayClass}
            style={zIndex ? { zIndex } : undefined}
            onClick={handleClose}
            onMouseDown={(e) => e.stopPropagation()}
        >
            <div
                className={containerClass}
                style={containerStyle}
                ref={isMobile ? sheetRef : undefined}
                onClick={(e) => e.stopPropagation()}
                onTouchStart={isMobile ? handleTouchStart : undefined}
                onTouchMove={isMobile ? handleTouchMove : undefined}
                onTouchEnd={isMobile ? handleTouchEnd : undefined}
            >
                {isMobile && (
                    <div className="bs-drag-handle">
                        <div className="bs-handle-bar" />
                    </div>
                )}
                {content}
            </div>
        </div>,
        document.body
    );
}
